// Core lib dependencies.
import Enums from '../config/enums.js';

import _ from 'lodash';

/**
 * <pre>
 * Privacy service.
 *
 * Summary of responsibilities:
 *
 *  - provides abstraction layer for managing privacy consent settings (cookies, storage, analytics, etc.)
 * </pre>
 *
 * @author Various
 * @namespace Common.Services.PrivacyService
 */
const PrivacyService = (function () {
  ('use strict');

  // Configuration service;
  let _configService;
  let _storageService;

  let _cookieDefaults;
  let _policyDate;
  let _isNeedingNecessaryCookies = false;
  let _isFirstVisit = false;

  // If privacy management is set to none, then privacy management will be fully disabled.  No UI, no API, no code gating.
  let _isPrivacyMgmtEnabled = true;

  // If this campaign uses custom privacy management, enable API, but disable the UI.
  let _isPrivacyUIEnabled = false;

  // Preview and staging do not gate code.
  let _isPreviewOrStaging = false;

  // *********
  // IMPORTANT - when we add cookies / local storage keys to our app, we should be sure to update this regex list.
  // *********
  //
  // If the user revokes a privacy type, we'll delete any existing cookies / local storage
  // Some items use [._] in the regex because our storage service replaces . with _ for cookies but not local storage. :(
  //
  // NOTE: In case you're wondering why there is a nextgen cookie here, it is because a user will either go to macro OR
  // nextgen for their first visit to our experiences, and the very first visit should clean up any existing cookies.  If
  // macro doesn't clean nextgen (and vice versa), then some cookies will never get cleaned up.
  const ngxCookiesRegex = {
    [Enums.PRIVACY_TYPES.NECESSARY]: [
      // just here for completeness, we won't delete these
      '^ngx_v$', // ngx_v (voting)
      '^ngx[._]auth[._]redirect.*', // starts with 'ngx.auth.redirect', the . could be an _ (authentication)
      '^gate_\\d+', // starts with 'gate_' ends with a number (DOB COPPA)
      '^\\d+_is_blocked', // NextGen cookie for DOB COPPA (deleting here on first visit only).
      '^ngx_consent$', // ngx_consent (privacy management consents)
    ],
    [Enums.PRIVACY_TYPES.FUNCTIONAL]: [
      '^ngx[._]progressive$', // ngx_progressive, the . could be an _ (progressively filtered form fields)
      '^\\d+_+[\\w-]+', // starts with number, at least one _, ends w/ combo of letters, digits, _, and - (progressivly filtered multi-optin)
    ],
    [Enums.PRIVACY_TYPES.ANALYTICS]: [
      '^ngx$', // ngx (helios entry tracking)
    ],
    [Enums.PRIVACY_TYPES.MARKETING]: [],
  };

  // These are our default consent settings.
  const _defaultConsents = {
    [Enums.PRIVACY_TYPES.NECESSARY]: true, // This one should never change.
    [Enums.PRIVACY_TYPES.FUNCTIONAL]: false,
    [Enums.PRIVACY_TYPES.MARKETING]: false,
    [Enums.PRIVACY_TYPES.ANALYTICS]: false,
  };

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  /**
   * Init.
   *
   * @memberOf Common.Services.PrivacyService
   *
   * @param configService {Common.Services.ConfigService} The config service.
   * @param storageService {Common.Services.StorageService} The storage service.
   */
  const init = (configService, storageService) => {
    _configService = configService;
    _storageService = storageService;

    _cookieDefaults = _configService.get('privacy.cookieSettings');
    _policyDate = _configService.get('privacy.policyDate');

    _isPrivacyMgmtEnabled = _configService.get('privacy.isPrivacyMgmtEnabled');
    _isPrivacyUIEnabled = _configService.get('privacy.isPrivacyUIEnabled');
    _isPreviewOrStaging = _configService.get('privacy.isPreviewOrStaging');
    _isNeedingNecessaryCookies = _doesCampaignNeedNecessaryCookies();

    // Check if there is a valid consent cookie, if not, delete all non-necessary cookies.  This is to account for the
    // scenario where a user had cookies stored prior to us asking for consents.
    const consentExists = doesValidConsentExist();
    if (!consentExists && _isPrivacyMgmtEnabled) {
      _isFirstVisit = true;

      // Force strictly necessary to be false in this temporary variable to force cleaning up of ALL experiences cookies.
      _cleanupRevokedCookies(Object.assign({}, _defaultConsents, { 'strictly necessary': false }));

      // If privacy managment is API only, initialize with the default consents since we are unable to ask.
      if (!_isPrivacyUIEnabled) setPrivacySettings(_defaultConsents);
    }
  };

  /**
   * Private - Concatenate all the revoked privacy type regexs and join them into one massive regex.
   * @param {Array} privacyTypes - array of privacy type strings
   * @return {RegExp} RegExp object containing all matchers ORed together into one full regex.
   */
  const _getRegexByPrivacyType = (privacyTypes) => {
    const matchesArray = privacyTypes.reduce(
      (acc, revokedType) => (ngxCookiesRegex[revokedType].length > 0 ? acc.concat(ngxCookiesRegex[revokedType]) : acc),
      []
    );
    return matchesArray.length === 0 ? null : new RegExp(matchesArray.join('|'), 'i');
  };

  /**
   * Private - If the user revokes a privacy type, clean up (delete) any existing cookies / local storage of that type.
   * @param {Object} currentConsents - consent settings object, all privacy types set to false will be deleted
   */
  const _cleanupRevokedCookies = (currentConsents) => {
    // If privacy management is disabled, then don't delete existing cookies.
    if (!_isPrivacyMgmtEnabled) return;

    const revokedConsents = Object.keys(currentConsents).filter((key) => currentConsents[key] === false);
    if (revokedConsents.length === 0) return;

    const regexMatcher = _getRegexByPrivacyType(revokedConsents);
    if (regexMatcher) _storageService.clearByRegex(regexMatcher);
  };

  /**
   * Private - Get the current policy date from the config service.
   * @returns {Date} Version date of privacy policy to trigger privacy consent popup when it changes.
   */
  const _getPolicyDate = () => {
    return { policyVersion: _policyDate };
  };

  /**
   * Private.  Does this campaign have any of the following situations?  If so, we MUST have strictly necessary cookies,
   * otherwise block the campaign.
   *
   *  - campaign has voting
   *  - campaign has social or custom authentication
   *  - campaign has COPPA enabled on a DOB
   *
   * @memberOf Common.Services.PrivacyService
   *
   * @returns {boolean} true if we MUST have necessary cookies
   */
  const _doesCampaignNeedNecessaryCookies = () => {
    // This campaign has a DOB with COPPA enabled? We MUST have necessary cookies allowed in order to age gate
    const _hasCoppaDOB = document.querySelector('[data-ngx-coppa="enabled"]') !== null;

    // This campaign has custom or social authentication?  We MUST have necessary cookies to handle redirections
    const _hasAuthentication =
      _configService.get('auth.authConfig')?.strategy &&
      _configService.get('auth.authConfig')?.strategy !== 'strategy.socialauth.disabled';

    // This campaign has some kind of voting that uses ngx_v, we need necessary cookies to track vote frequency
    const _hasVoting = _configService.get('strategies.voting') !== 'strategy.voting.none';

    return _hasCoppaDOB || _hasAuthentication || _hasVoting;
  };

  /**
   * Private - Check the incoming privacy consent values for non-recognized values & ensure NECESSARY never changes from default.
   * @returns {Object} Sanitized copy of settings object.
   */
  const _sanitizePrivacyConsents = (settings) => {
    const sanitizedSettings = {};
    const whitelist = Object.values(Enums.PRIVACY_TYPES);
    for (const key in settings) {
      if (!whitelist.includes(key)) {
        console.warn(
          `Warning: Privacy consent type '${key}' not recognized. Please use one of: ${whitelist.join(', ')}.`
        );
      } else if (
        key === Enums.PRIVACY_TYPES.NECESSARY &&
        settings[key] !== _defaultConsents[Enums.PRIVACY_TYPES.NECESSARY]
      ) {
        console.warn(
          `Warning: Privacy consent type '${key}' must always be ${_defaultConsents[Enums.PRIVACY_TYPES.NECESSARY]}.`
        );
        sanitizedSettings[key] = _defaultConsents[Enums.PRIVACY_TYPES.NECESSARY];
      } else {
        sanitizedSettings[key] = settings[key];
      }
    }
    return sanitizedSettings;
  };

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Api Methods.

  /**
   * Check if we need to do any type of privacy management at all.
   *
   * @memberOf Common.Services.PrivacyService
   *
   * @returns {boolean} true if privacy management is enabled, false if it is completely disabled.
   */
  const isPrivacyManagementEnabled = function () {
    return _isPrivacyMgmtEnabled;
  };

  /**
   * Check if we need to show the privacy management UI
   *
   * @memberOf Common.Services.PrivacyService
   *
   * @returns {boolean} true to show the quick consent bar, popup or settings button, false to hide both
   */
  const isPrivacyManagementUIEnabled = function () {
    return _isPrivacyUIEnabled;
  };

  /**
   * Check if the user has visited one of our experiences before by checking for a consent cookie.
   *
   * @memberOf Common.Services.PrivacyService
   *
   * @returns {boolean} true if the user has NEVER previously visited which would save consent settings.
   */
  const isFirstVisit = function () {
    return _isFirstVisit;
  };

  /**
   * Check if we need to show the cookie consent popup to this user.
   *
   * Show it if:
   * - there is no existing consent settings cookie.
   * - the cookie doesn't have a policy date for whatever reason
   * - the cookie policy date doesn't match the current policy date (for policy changes)
   * - previously set cookie has expired (after time span specified in config)
   *
   * @memberOf Common.Services.PrivacyService
   *
   * @returns {boolean} true to show the popup
   */
  const doesValidConsentExist = function () {
    // Does it exist and can we parse it?
    let doesExist = _storageService.get(_cookieDefaults.name);
    try {
      doesExist = doesExist ? JSON.parse(doesExist) : false;
    } catch (e) {
      doesExist = false;
    }

    // Check if policy change happened and we need to force display
    if (
      doesExist &&
      (!doesExist.policyVersion || new Date(doesExist.policyVersion).getTime() !== _policyDate.getTime())
    ) {
      return false;
    }

    return !!doesExist;
  };

  /**
   * Set user's privacy settings with their chosen values
   *
   * @memberOf Common.Services.PrivacyService
   *
   * @param newSettings {Object} Contains properties for cookie consent choices. Send empty object to set to defaults.
   */
  const setPrivacySettings = (newSettings) => {
    // If privacy management is disabled, then don't set privacy settings.
    if (!_isPrivacyMgmtEnabled) return;

    const currentSettings = getPrivacySettings();

    // Drop out any non-recognised cookie consent settings & ensure NECESSARY never changes.
    const sanitizedSettings = _sanitizePrivacyConsents(newSettings);

    // Merge with the defaults
    const allSettings = Object.assign({}, _defaultConsents, sanitizedSettings);

    // Compare old and new settings. Find any falses that are now true & send an event, so we can restart anything
    // that needs restarting (like analytics).
    const falseConsents = _.keys(_.pickBy(currentSettings, (value) => !value));
    const trueConsents = _.keys(_.pickBy(allSettings));
    const intersection = _.intersection(falseConsents, trueConsents);

    // Merge in policy date
    const allSettingsAndPolicy = Object.assign({}, allSettings, _getPolicyDate());

    // Set the consent cookie / local storage.  This is considered 'strictly necessary'.
    const expirationDate = new Date();
    expirationDate.setDate(expirationDate.getDate() + _cookieDefaults.expirationDays);
    _storageService.set(_cookieDefaults.name, JSON.stringify(allSettingsAndPolicy), 'both', expirationDate);

    // Trigger a re-enable event for any previously-revoked and now consented-to privacy types.
    intersection.forEach((consentType) => {
      NGX.App.Events.trigger('privacy:consent:activated', { consentType });
    });

    // For all falses (consent not given), remove any existing cookies / local storage keys of that type.
    _cleanupRevokedCookies(allSettings);
  };

  /**
   * Get cookie consent values if set, or get defaults.
   *
   * @memberOf Common.Services.PrivacyService
   *
   * @returns {Object} mapping of cookie types to boolean consent values, empty object if privacy management is not enabled.
   */
  const getPrivacySettings = () => {
    // If privacy management is disabled, don't return settings.
    if (!_isPrivacyMgmtEnabled) return {};

    let currentVal = _storageService.get(_cookieDefaults.name);

    // Strip out the policy date.
    if (currentVal) {
      currentVal = JSON.parse(currentVal);
      delete currentVal.policyVersion;
    }

    return currentVal || _defaultConsents;
  };

  /**
   * Check if we have user consent to set cookies of a given type.
   * See Enums.PRIVACY_TYPES.
   *
   * @memberOf Common.Services.PrivacyService
   *
   * @param {string} cookieType String containing the type of cookie we want to check
   * @returns {boolean} true for consent is given for cookieType
   */
  const doAllowActionType = (cookieType) => {
    // If privacy management is disabled, don't gate any code. Also, don't do any gating for preview or staging.
    if (!_isPrivacyMgmtEnabled || _isPreviewOrStaging) return true;

    const currentSettings = getPrivacySettings();
    if (!currentSettings.hasOwnProperty(cookieType)) {
      console.warn(
        `Warning: PrivacyService cannot check unknown cookie type: "${cookieType}". Valid cookie types are: ${Object.keys(
          currentSettings
        ).join(', ')}`
      );
    }

    return currentSettings[cookieType];
  };

  /**
   * Has the user disabled cookies in their browser?  If so, we can't set our necessary cookies, show cookies disabled modal.
   *
   * @memberOf Common.Services.PrivacyService
   *
   * @param {string} cookieType String containing the type of cookie we want to check
   * @returns {boolean} true for consent is given for cookieType
   */
  const doesBrowserSupportCookies = () => {
    let localStorageEnabled;
    try {
      localStorageEnabled = typeof localStorage !== 'undefined';
    } catch (exc) {
      localStorageEnabled = false;
    }

    return navigator.cookieEnabled || localStorageEnabled;
  };

  /**
   * Do we need to show the cookies disabled modal?  Only show if browser has cookies disabled _AND_:
   *  - campaign has voting
   *  - campaign has social or custom authentication
   *  - campaign has COPPA enabled on a DOB
   *
   * @memberOf Common.Services.PrivacyService
   *
   * @returns {boolean} true if we need to show the cookies disabled modal
   */
  const isCookiesDisabledModalNeeded = () => {
    // If privacy management is disabled, never show the cookies disabled modal.
    if (!_isPrivacyMgmtEnabled) return false;

    if (doesBrowserSupportCookies()) return false;

    return _isNeedingNecessaryCookies;
  };

  // Expose scoped properties.
  return {
    init,

    get: getPrivacySettings,
    set: setPrivacySettings,

    isPrivacyManagementEnabled,
    isPrivacyManagementUIEnabled,
    isFirstVisit,
    doesValidConsentExist,
    doAllowActionType,
    doesBrowserSupportCookies,
    isCookiesDisabledModalNeeded,
  };
})();

export default PrivacyService;
